import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import Boton from '../components/Boton'
import { resenasApi } from '../api/resenas'
import { fmtFechaHora } from '../utils/fecha'
import { colors, text, space, sx, radius } from '../design/tokens'

/**
 * CLIENTES → Reseñas de productos (Martin, 10 sep 2026). Lo que cada cliente
 * dijo de cada botella desde el mail de los 15 días (estrellas + comentario),
 * y la decisión de publicar u ocultar en la ficha del store. Las de 4-5
 * estrellas sin texto se publican solas; el resto espera acá. Una reseña mala
 * nunca se borra: se decide si se publica.
 */
const ESTADOS = [
  { id: 'pendiente', label: 'Para revisar' },
  { id: 'publicada', label: 'Publicadas' },
  { id: 'oculta',    label: 'Ocultas' },
  { id: 'todas',     label: 'Todas' },
]

const ESTADO_BADGE = {
  pendiente: { bg: '#fef3c7', fg: '#92400e', label: 'Para revisar' },
  publicada: { bg: '#dcfce7', fg: '#166534', label: 'Publicada' },
  oculta:    { bg: colors.bgSoft, fg: colors.textSec, label: 'Oculta' },
}

function Estrellas({ n }) {
  if (!n) return <span style={{ color: colors.textSub }}>—</span>
  return (
    <span style={{ ...sx.mono, whiteSpace: 'nowrap' }} title={`${n} de 5`}>
      <span style={{ color: '#2D6A3F', letterSpacing: 1 }}>{'★'.repeat(n)}</span>
      <span style={{ color: colors.border, letterSpacing: 1 }}>{'★'.repeat(5 - n)}</span>
    </span>
  )
}

export default function ResenasProductosPage() {
  const [estado, setEstado] = useState('pendiente')
  const [q, setQ] = useState('')
  const [filas, setFilas] = useState([])
  const [conteos, setConteos] = useState({ pendiente: 0, publicada: 0, oculta: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [trabajando, setTrabajando] = useState(null)

  const cargar = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const { data } = await resenasApi.productos({ estado, q: q || undefined })
      setFilas(data.data || [])
      setConteos(data.conteos || {})
    } catch {
      setError('No se pudieron cargar las reseñas.')
    } finally { setLoading(false) }
  }, [estado, q])

  useEffect(() => { cargar() }, [cargar])

  const accion = async (r, que) => {
    setTrabajando(r.id); setError('')
    try {
      await (que === 'publicar' ? resenasApi.publicarProducto(r.id) : resenasApi.ocultarProducto(r.id))
      await cargar()
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo aplicar.')
    } finally { setTrabajando(null) }
  }

  const total = (conteos.pendiente || 0) + (conteos.publicada || 0) + (conteos.oculta || 0)

  return (
    <div>
      <div style={st.header}>
        <div>
          <h1 style={st.title}>Reseñas de productos</h1>
          <div style={st.sub}>
            Estrellas y comentarios por botella, del mail de los 15 días. Las de 4 o 5 estrellas sin texto se publican solas en la ficha del store; el resto espera acá.
          </div>
        </div>
      </div>

      {error && <div style={st.errorBox}>{error}</div>}

      <div style={st.toolbar}>
        <div style={st.chips}>
          {ESTADOS.map(e => {
            const n = e.id === 'todas' ? total : (conteos[e.id] || 0)
            const on = estado === e.id
            return (
              <button key={e.id} type="button" onClick={() => setEstado(e.id)}
                style={{ ...st.chip, ...(on ? st.chipOn : {}) }}>
                {e.label}
                <span style={{ ...st.chipN, ...(on ? st.chipNOn : {}) }}>{n}</span>
              </button>
            )
          })}
        </div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar en comentarios, producto o cliente…" style={st.search} />
      </div>

      <div style={{ ...sx.card, padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={st.empty}>Cargando…</div>
        ) : filas.length === 0 ? (
          <div style={st.empty}>
            {estado === 'pendiente' ? 'No hay reseñas para revisar.' : 'No hay reseñas en esta vista.'}
          </div>
        ) : (
          <table style={sx.table}>
            <thead>
              <tr>
                <th style={sx.th}>Cuándo</th>
                <th style={sx.th}>Producto</th>
                <th style={sx.th}>Estrellas</th>
                <th style={sx.th}>Comentario</th>
                <th style={sx.th}>Cliente</th>
                <th style={sx.th}>Estado</th>
                <th style={{ ...sx.th, textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {filas.map(r => {
                const b = ESTADO_BADGE[r.estado] || ESTADO_BADGE.pendiente
                const cuando = r.comentario_at && (!r.puntuada_at || r.comentario_at > r.puntuada_at) ? r.comentario_at : r.puntuada_at
                return (
                  <tr key={r.id} style={st.tr}>
                    <td style={{ ...sx.td, ...sx.mono, color: colors.textSec, whiteSpace: 'nowrap' }}>{cuando ? fmtFechaHora(cuando) : '—'}</td>
                    <td style={sx.td}>
                      <div style={st.prod}>{r.articulo?.descripcion || r.articulo?.sku}</div>
                      <div style={{ ...sx.mono, fontSize: text.meta, color: colors.textSub }}>{r.articulo?.sku}</div>
                    </td>
                    <td style={sx.td}><Estrellas n={r.estrellas} /></td>
                    <td style={{ ...sx.td, maxWidth: 420, whiteSpace: 'pre-wrap', lineHeight: 1.4, padding: `${space.md}px ${space.xl}px` }}>
                      {r.comentario || <span style={{ color: colors.textSub }}>—</span>}
                    </td>
                    <td style={sx.td}>
                      {r.cliente ? <Link to={`/clientes/${r.cliente.id}`} style={st.link}>{r.cliente.nombre}</Link> : '—'}
                      <div style={{ fontSize: text.meta, color: colors.textSub }}>
                        firma «{r.nombre_publico || 'Cliente'}»
                        {r.nota_venta ? <> · <Link to={`/notas-venta/${r.nota_venta.id}`} style={st.link}>{r.nota_venta.numero}</Link></> : null}
                      </div>
                    </td>
                    <td style={sx.td}>
                      <span style={{ ...sx.badge, background: b.bg, color: b.fg }}>{b.label}</span>
                      {r.revisada_por && <div style={{ fontSize: text.meta, color: colors.textSub }}>{r.revisada_por}</div>}
                    </td>
                    <td style={{ ...sx.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {r.estado !== 'publicada' && r.estrellas && (
                        <Boton variante="primary" sm disabled={trabajando === r.id} onClick={() => accion(r, 'publicar')}>Publicar</Boton>
                      )}
                      {r.estado !== 'oculta' && (
                        <Boton variante="secondary" sm disabled={trabajando === r.id} onClick={() => accion(r, 'ocultar')} style={{ marginLeft: 6 }}>Ocultar</Boton>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

const st = {
  header:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: space.lg, marginBottom: space.lg },
  title:    { margin: 0, fontSize: text.h1, fontWeight: 600, color: colors.text },
  sub:      { marginTop: 4, fontSize: text.sec, color: colors.textSec, maxWidth: 760 },
  errorBox: { background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: radius.md, marginBottom: space.lg, fontSize: text.body },
  toolbar:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: space.lg, flexWrap: 'wrap', marginBottom: space.md },
  chips:    { display: 'flex', gap: 6, flexWrap: 'wrap' },
  chip:     { appearance: 'none', border: `1px solid ${colors.border}`, background: colors.bg, color: colors.textSec, borderRadius: radius.pill, padding: '4px 10px', fontSize: text.sec, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'inherit' },
  chipOn:   { background: colors.text, color: colors.bg, borderColor: colors.text },
  chipN:    { fontFamily: 'inherit', fontVariantNumeric: 'tabular-nums', background: colors.bgSoft, color: colors.textSub, borderRadius: radius.pill, padding: '0 6px', fontSize: text.meta },
  chipNOn:  { background: 'rgba(255,255,255,.18)', color: colors.bg },
  search:   { padding: '6px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: text.body, minWidth: 280, fontFamily: 'inherit' },
  empty:    { padding: space.xl, color: colors.textSec, fontSize: text.body, textAlign: 'center' },
  tr:       { borderBottom: `1px solid ${colors.border}`, height: 44 },
  prod:     { fontWeight: 600, color: colors.text, fontSize: text.body },
  link:     { color: colors.link, textDecoration: 'none' },
}
